import React, { useContext, useMemo } from "react";
import {
  Box,
  Grid,
  Card,
  Typography,
  Stack,
  CircularProgress,
  Chip,
  Button,
  Divider,
  Alert,
  AlertTitle,
} from "@mui/material";
import { Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import WarningIcon from "@mui/icons-material/Warning";
import { UserContext } from "../context/UserProvider";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import useApiEp from "../hooks/useApiEp";
import { getErrorResponse } from "../utils/util";
import PhoneIcon from "@mui/icons-material/Phone";
import VerifiedIcon from "@mui/icons-material/Verified";
import BadgeIcon from "@mui/icons-material/Badge";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChatIcon from "@mui/icons-material/Chat";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import FacebookIcon from "@mui/icons-material/Facebook";
import StatsCards from "./DashStats";
import ListDataItems from "./ListDataItems";
import MessageVolumeCard from "./MessageVolumeCard";
import WeeklyCreditsCard from "./WeeklyCreditsCard";
import { useAccount } from "../context/AccountContext";

const pieColors = [
  "#4f46e5",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#06b6d4",
];
export const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const WEEKLY_CREDITS = [
  { day: "Mon", credits: 320 },
  { day: "Tue", credits: 450 },
  { day: "Wed", credits: 380 },
  { day: "Thu", credits: 610 },
  { day: "Fri", credits: 290 },
  { day: "Sat", credits: 470 },
  { day: "Sun", credits: 510 },
];

export default function WabaDashboard() {
  const { selectedAccount } = useAccount();
  const { user, loading } = useContext(UserContext);
  const { data: AisensyBalance } = useApiEp(
    `/user/get-aisensy-partner-details`,
    user?.userType === "Root" || user?.userType === "SuperAdmin"
  );
  const { data: TemplateStats } = useApiEp(`/api/get-template-category-stats`);
  const {
    data: PhoneData,
    isLoading: isPhoneLoading,
    isError: isPhoneError,
    error: phoneError,
  } = useApiEp(`/user/phone-details?cid=${selectedAccount}`);

  // Modern light color palette with vibrant accents
  const colors = {
    primary: "#4f46e5",
    secondary: "#7c3aed",
    success: "#10b981",
    warning: "#f59e0b",
    error: "#ef4444",
    info: "#06b6d4",
    background: "#f8fafc",
    cardBg: "#ffffff",
    textPrimary: "#1e293b",
    textSecondary: "#64748b",
    accent: "#8b5cf6",
  };

  const templateUsage = useMemo(() => {
    if (!TemplateStats) {
      return [];
    } else {
      return TemplateStats.map((item) => ({
        name: item.category,
        value: item.count,
      }));
    }
  }, [TemplateStats]);

  return (
    <Box
      sx={{
        width: "100%",
        p: { xs: 2, md: 4 },
        display: "flex",
        flexDirection: "column",
        gap: 4,
        background: `
          linear-gradient(135deg, 
            rgba(248, 250, 252, 0.95) 0%, 
            rgba(241, 245, 249, 0.9) 50%,
            rgba(226, 232, 240, 0.8) 100%
          ),
          radial-gradient(
            circle at 20% 80%, 
            rgba(99, 102, 241, 0.08) 0%, 
            transparent 50%
          ),
          radial-gradient(
            circle at 80% 20%, 
            rgba(139, 92, 246, 0.05) 0%, 
            transparent 50%
          )
        `,
        minHeight: "100vh",
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "400px",
          background: `
            radial-gradient(
              ellipse at top center,
              rgba(99, 102, 241, 0.06) 0%,
              transparent 70%
            )
          `,
          zIndex: 0,
        },
      }}
    >
      {/* Balance & Stats Cards */}
      <Grid container spacing={3}>
        {/* Balance Card */}
        <Grid item xs={12} md={7}>
          <Card
            sx={{
              p: 3,
              borderRadius: 3,
              background: `
        linear-gradient(135deg, 
          rgba(255, 255, 255, 0.95) 0%, 
          rgba(248, 250, 252, 0.9) 100%
        )
      `,
              backdropFilter: "blur(20px)",
              boxShadow: `
        0 8px 32px rgba(99, 102, 241, 0.1),
        0 2px 8px rgba(0, 0, 0, 0.05),
        inset 0 1px 0 rgba(255, 255, 255, 0.8)
      `,
              border: "1px solid rgba(255, 255, 255, 0.4)",
              height: "100%",
            }}
          >
            <Box
              sx={{ display: "flex", flexDirection: "column", height: "100%" }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  mb: 3,
                  background:
                    "linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #c026d3 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                {user ? `Welcome back, ${user.customerId}! 👋` : "Loading..."}
              </Typography>

              <ListDataItems />

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mt: 3,
                  pt: 2,
                  borderTop: "1px solid rgba(0, 0, 0, 0.08)",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                }}
              >
                {user?.userType === "SuperAdmin" && (
                  <Chip
                    label="Super Admin Mode"
                    sx={{
                      background:
                        "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                      color: "#fff",
                      fontWeight: 600,
                      animation: "pulse 2s infinite",
                      "@keyframes pulse": {
                        "0%": { transform: "scale(1)" },
                        "50%": { transform: "scale(1.05)" },
                        "100%": { transform: "scale(1)" },
                      },
                    }}
                  />
                )}

                {user?.userType === "Root" ||
                user?.userType === "SuperAdmin" ? (
                  <>
                    {AisensyBalance && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            color: colors.textSecondary,
                            fontWeight: 500,
                            fontSize: "0.9rem",
                          }}
                        >
                          Balance:
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            color: colors.primary,
                            fontWeight: 700,
                            background:
                              "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                          }}
                        >
                          ₹{AisensyBalance.centralBalance.toFixed(2)}
                        </Typography>
                      </Box>
                    )}
                  </>
                ) : (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: colors.textSecondary,
                        fontWeight: 500,
                        fontSize: "0.85rem",
                      }}
                    >
                      Your plan expiring on:
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: colors.error, // or colors.primary if you prefer
                        fontWeight: 600,
                      }}
                    >
                      {user?.expiryDate
                        ? new Date(user.expiryDate).toLocaleDateString(
                            "en-IN",
                            {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            }
                          )
                        : "NA"}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} md={5}>
          <Card
            sx={{
              p: 2,
              borderRadius: 3,
              background: `
                linear-gradient(135deg, 
                  rgba(255, 255, 255, 0.95) 0%, 
                  rgba(248, 250, 252, 0.9) 100%
                )
              `,
              backdropFilter: "blur(20px)",
              boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
              border: "1px solid rgba(255, 255, 255, 0.4)",
              height: "100%",
            }}
          >
            {PhoneData ? (
              <Stack
                spacing={0}
                divider={
                  <Divider
                    sx={{
                      borderColor: "rgba(99, 102, 241, 0.08)",
                    }}
                  />
                }
              >
                {/* Account Status */}
                <Box sx={{ p: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        background:
                          PhoneData.data?.status === "CONNECTED"
                            ? "linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(34, 197, 94, 0.1) 100%)"
                            : "linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.1) 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {PhoneData.data?.status === "CONNECTED" ? (
                        <LinkIcon
                          sx={{
                            color: colors.success,
                            fontSize: "1.1rem",
                          }}
                        />
                      ) : (
                        <LinkOffIcon
                          sx={{
                            color: colors.error,
                            fontSize: "1.1rem",
                          }}
                        />
                      )}
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        color={colors.textSecondary}
                      >
                        Account Status
                      </Typography>
                      <Typography
                        fontSize={14}
                        color={colors.textPrimary}
                        fontWeight={500}
                      >
                        {PhoneData.data?.status || "Unknown"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Phone Number */}
                <Box sx={{ p: 1.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        background:
                          "linear-gradient(135deg, rgba(79, 70, 229, 0.15) 0%, rgba(124, 58, 237, 0.1) 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <PhoneIcon
                        sx={{
                          color: colors.primary,
                          fontSize: "1.1rem",
                        }}
                      />
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        color={colors.textSecondary}
                      >
                        Phone Number
                      </Typography>
                      <Typography
                        fontSize={14}
                        color={colors.textPrimary}
                        fontWeight={500}
                      >
                        {PhoneData.data?.display_phone_number}
                        {PhoneData.data?.is_official_business_account && (
                          <VerifiedIcon
                            sx={{
                              color: colors.success,
                              fontSize: "1rem",
                              ml: 0.5,
                              verticalAlign: "text-bottom",
                            }}
                          />
                        )}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Verified Name */}
                <Box sx={{ p: 1.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        background:
                          "linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(217, 119, 6, 0.1) 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <BadgeIcon
                        sx={{
                          color: colors.warning,
                          fontSize: "1.1rem",
                        }}
                      />
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        color={colors.textSecondary}
                      >
                        Verified Name
                      </Typography>
                      <Typography
                        fontSize={14}
                        color={colors.textPrimary}
                        fontWeight={500}
                      >
                        {PhoneData.data?.verified_name || "Not verified"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Quality Rating */}
                <Box sx={{ p: 1.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        background:
                          PhoneData.data?.quality_score?.score === "GREEN"
                            ? "linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(34, 197, 94, 0.1) 100%)"
                            : "linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(217, 119, 6, 0.1) 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {PhoneData.data?.quality_score?.score === "GREEN" ||
                      PhoneData.data?.quality_rating === "GREEN" ? (
                        <CheckCircleIcon
                          sx={{
                            color: colors.success,
                            fontSize: "1.1rem",
                          }}
                        />
                      ) : (
                        <WarningIcon
                          sx={{
                            color: colors.warning,
                            fontSize: "1.1rem",
                          }}
                        />
                      )}
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        color={colors.textSecondary}
                      >
                        Quality Rating
                      </Typography>
                      <Typography
                        fontSize={14}
                        color={colors.textPrimary}
                        fontWeight={500}
                      >
                        {PhoneData.data?.quality_score?.score ||
                          PhoneData.data?.quality_rating ||
                          "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Messaging Limit */}
                <Box sx={{ p: 1.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        background:
                          "linear-gradient(135deg, rgba(6, 182, 212, 0.15) 0%, rgba(8, 145, 178, 0.1) 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <ChatIcon
                        sx={{
                          color: colors.info,
                          fontSize: "1.1rem",
                        }}
                      />
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        color={colors.textSecondary}
                      >
                        Messaging Limit
                      </Typography>
                      <Typography
                        fontSize={14}
                        color={colors.textPrimary}
                        fontWeight={500}
                      >
                        {PhoneData.data?.whatsapp_business_manager_messaging_limit?.replace(
                          "TIER_",
                          ""
                        ) || "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Stack>
            ) : isPhoneLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  py: 3,
                }}
              >
                <CircularProgress
                  size={28}
                  sx={{
                    color: colors.primary,
                    "& .MuiCircularProgress-circle": {
                      strokeWidth: 3,
                    },
                  }}
                />
              </Box>
            ) : isPhoneError ? (
              <Alert severity="error">{getErrorResponse(phoneError)}</Alert>
            ) : (
              <Alert severity="info">No data!</Alert>
            )}
            {!user?.partner &&
              !isPhoneLoading &&
              PhoneData?.data?.status !== "CONNECTED" && (
                <Stack direction={"column"} spacing={1}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<FacebookIcon />}
                    onClick={() => {
                      localStorage.setItem("cusId", user.customerId);
                      localStorage.setItem("partner", "haptik");
                      window.launchWhatsAppSignup("1108770184427834");
                    }}
                    sx={{
                      mt: 3,
                      background:
                        "linear-gradient(135deg, #1877f2 0%, #165fe5 100%)",
                      color: "white",
                      borderRadius: "12px",
                      py: 1.5,
                      textTransform: "none",
                      fontWeight: 600,
                      boxShadow: "0 4px 12px rgba(24, 119, 242, 0.3)",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #165fe5 0%, #144fd1 100%)",
                        transform: "translateY(-1px)",
                        boxShadow: "0 6px 16px rgba(24, 119, 242, 0.4)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    Link your Phone Option1
                  </Button>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<FacebookIcon />}
                    onClick={() => {
                      localStorage.setItem("cusId", user.customerId);
                      localStorage.setItem("partner", "aisensy");
                      window.launchWhatsAppSignup("1240838350783888");
                    }}
                    sx={{
                      mt: 3,
                      background:
                        "linear-gradient(135deg, #1877f2 0%, #165fe5 100%)",
                      color: "white",
                      borderRadius: "12px",
                      py: 1.5,
                      textTransform: "none",
                      fontWeight: 600,
                      boxShadow: "0 4px 12px rgba(24, 119, 242, 0.3)",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #165fe5 0%, #144fd1 100%)",
                        transform: "translateY(-1px)",
                        boxShadow: "0 6px 16px rgba(24, 119, 242, 0.4)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    Link your Phone Option2
                  </Button>
                </Stack>
              )}
          </Card>
        </Grid>
      </Grid>
      <StatsCards />

      {/* Charts Section */}
      <Grid container spacing={3}>
        {/* Monthly Messages Chart */}
        <Grid item xs={12} lg={7}>
          <MessageVolumeCard
            title="Monthly Message Volume"
            chipLabel="2025"
            colors={{
              primary: "#4f46e5",
              secondary: "#6366f1",
              textPrimary: "#0f172a",
              textSecondary: "#64748b",
            }}
          />
        </Grid>

        {/* Template Usage Chart */}
        <Grid item xs={12} lg={5}>
          <Card
            sx={{
              p: 3,
              borderRadius: 3,
              background: `
                linear-gradient(135deg, 
                  rgba(255, 255, 255, 0.95) 0%, 
                  rgba(248, 250, 252, 0.9) 100%
                )
              `,
              backdropFilter: "blur(20px)",
              boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
              border: "1px solid rgba(255, 255, 255, 0.4)",
              height: "100%",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: colors.textPrimary,
                mb: 3,
                "&::after": {
                  content: '""',
                  display: "block",
                  width: "48px",
                  height: "3px",
                  background: `linear-gradient(90deg, ${colors.secondary}, transparent)`,
                  borderRadius: "3px",
                  mt: 1,
                },
              }}
            >
              Template Category Distribution
            </Typography>

            <Box sx={{ position: "relative", height: "260px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={templateUsage}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={90}
                    paddingAngle={2}
                    labelLine={false}
                    label={({ name, percent }) =>
                      `${name} ${(percent * 100).toFixed(0)}%`
                    }
                  >
                    {templateUsage.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={pieColors[index % pieColors.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      background: "rgba(255, 255, 255, 0.95)",
                      border: `1px solid ${colors.primary}30`,
                      borderRadius: "12px",
                      boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                      color: colors.textPrimary,
                      backdropFilter: "blur(20px)",
                    }}
                    itemStyle={{
                      color: colors.textPrimary,
                      fontSize: "14px",
                      fontWeight: "500",
                    }}
                    labelStyle={{
                      color: colors.primary,
                      fontSize: "14px",
                      fontWeight: "600",
                      marginBottom: "8px",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>

              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  textAlign: "center",
                }}
              >
                <Typography variant="body2" color={colors.textSecondary}>
                  Total Usage
                </Typography>
                <Typography
                  variant="h4"
                  color={colors.primary}
                  fontWeight={700}
                >
                  {templateUsage.reduce((sum, item) => sum + item.value, 0)}%
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                gap: 1.5,
                mt: 3,
              }}
            >
              {templateUsage.map((item, index) => (
                <Box key={index} sx={{ display: "flex", alignItems: "center" }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "2px",
                      background: pieColors[index % pieColors.length],
                      mr: 1,
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                  />
                  <Typography variant="body2" color={colors.textSecondary}>
                    {item.name}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Credits Usage Chart */}
      <WeeklyCreditsCard colors={colors} />
    </Box>
  );
}
